export const roundToDecimal = (num: number, decimalPlaces: number): number => {
  return Math.round(num * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};

export const getRandomDecimal = (
  {
    start = 0,
    end = 1,
    decimalPlaces = 2,
  }: {
    start?: number;
    end?: number;
    decimalPlaces?: number;
  } = {
    start: 0,
    end: 1,
    decimalPlaces: 2,
  },
) => {
  const randomNumber = Math.random() * (end - start) + start;
  return roundToDecimal(randomNumber, decimalPlaces);
};

/**
 * @param num - The number to format.
 * @returns The formatted number as a string.
 */
export function formatNumber(num: number): string {
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';

  // Browser don't seem to recognize 'is' as a valid locale, so we'll use 'de-DE' instead
  const effectiveLocale = locale.toLowerCase().startsWith('is') ? 'de-DE' : locale;

  return new Intl.NumberFormat(effectiveLocale).format(num);
}

export const useLocaleSeparators = () => {
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';

  // Browser don't seem to recognize 'is' as a valid locale, so we'll use 'de-DE' instead
  const effectiveLocale = locale.toLowerCase().startsWith('is') ? 'de-DE' : locale;

  const parts = new Intl.NumberFormat(effectiveLocale, { useGrouping: true }).formatToParts(1000.1);
  const group = parts.find((p) => p.type === 'group')?.value ?? ',';
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.';

  return { group, decimal };
};

/**
 * Parse a localized numeric string using provided group and decimal separators.
 * - Strips grouping and whitespace
 * - Normalizes decimal to '.'
 * - Collapses multiple decimals
 * - Returns NaN if not a valid number
 */
export function parseLocalizedNumber(
  inputText: string,
  groupSeparator?: string,
  decimalSeparator?: string,
): number {
  if (inputText == null || inputText === '') return NaN;
  let numberString = String(inputText);
  const isNegative = numberString.trim().startsWith('-');
  // Preserve leading minus sign for negative numbers
  if (isNegative) {
    numberString = numberString.trim().slice(1);
  }
  // Remove locale-specific grouping separators (escape for regex, then strip globally)
  if (groupSeparator) {
    const escapedGroupSeparator = groupSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    numberString = numberString.replace(new RegExp(escapedGroupSeparator, 'g'), '');
  }
  // Strip all whitespace (including non‑breaking spaces) before parsing
  numberString = numberString.replace(/[\s\u00A0]/g, '');
  // Normalize decimal separator: escape the locale-specific separator and replace all with '.'
  if (decimalSeparator && decimalSeparator !== '.') {
    const escapedDecimalSeparator = decimalSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    numberString = numberString.replace(new RegExp(escapedDecimalSeparator, 'g'), '.');
  }
  // Drop any characters that are neither digits nor the normalized decimal point
  numberString = numberString.replace(/[^0-9.]/g, '');
  const firstDot = numberString.indexOf('.');
  // Collapse multiple decimal points: keep the first '.' and remove any subsequent ones
  if (firstDot !== -1) {
    numberString =
      numberString.slice(0, firstDot + 1) + numberString.slice(firstDot + 1).replace(/\./g, '');
  }
  // If there are no digits at all (e.g., input was just '-'), treat as NaN
  if (!/[0-9]/.test(numberString)) return NaN;
  const parsedNumber = Number(numberString);
  if (!Number.isFinite(parsedNumber)) return NaN;
  return isNegative ? -parsedNumber : parsedNumber;
}

export const hasNumericValue = (value: number | undefined | null): value is number => {
  return value !== undefined && value !== null && Number.isFinite(value);
};
